import type { ChannelConfig, CreateChannelConfigRequest, UpdateChannelConfigRequest, TestChannelResponse, MessagesResponse } from '../types/messaging.js';

const BASE_URL = '/api/messaging';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getChannels(): Promise<ChannelConfig[]> {
  return fetchJson<ChannelConfig[]>(`${BASE_URL}/channels`);
}

export async function getChannelsByCompany(companyId: number): Promise<ChannelConfig[]> {
  return fetchJson<ChannelConfig[]>(`${BASE_URL}/channels?company_id=${companyId}`);
}

export async function getChannel(id: number): Promise<ChannelConfig> {
  return fetchJson<ChannelConfig>(`${BASE_URL}/channels/${id}`);
}

export async function createChannel(data: CreateChannelConfigRequest): Promise<ChannelConfig> {
  return fetchJson<ChannelConfig>(`${BASE_URL}/channels`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateChannel(id: number, data: UpdateChannelConfigRequest): Promise<ChannelConfig> {
  return fetchJson<ChannelConfig>(`${BASE_URL}/channels/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteChannel(id: number): Promise<void> {
  const response = await fetch(`${BASE_URL}/channels/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
}

export async function testChannel(id: number): Promise<TestChannelResponse> {
  return fetchJson<TestChannelResponse>(`${BASE_URL}/channels/${id}/test`, {
    method: 'POST',
  });
}

export async function getMessages(limit = 50, offset = 0, channelConfigId?: number): Promise<MessagesResponse> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (channelConfigId !== undefined) {
    params.set('channel_config_id', String(channelConfigId));
  }
  return fetchJson<MessagesResponse>(`${BASE_URL}/messages?${params}`);
}
